import React, { useState, useRef } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  Menu,
  MenuItem,
  Popper,
  Paper,
  CircularProgress,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

/**
 * Document table for the Document Repository popup (PPT "Document Repository").
 * Presentational — selection state is owned by the parent (index.jsx). Two
 * behaviours live here:
 *   • Hovering the MOSYMPHONY ID opens a read-only "Extracted Fields" preview.
 *     Per the PPT redesign the Mosymphony ID (a unique id generated via
 *     Mosymphony) is its own column and the ONLY hover target — dotted underline
 *     marks it. DOCUMENT ID is now a plain column (some docs have one, some show
 *     "—"), no longer interactive.
 *   • The Action column is a "Select action" dropdown. Per the current scope,
 *     Review fields / Review queries are DISABLED (the review workspace is not
 *     in scope); only Download document is active.
 */
const DASH = '—';

const DocumentTable = ({
  classes,
  documents,
  loading,
  viewSaved,
  selected,
  onToggle,
  onToggleAll,
  onDownloadDocument,
}) => {
  const allSelected = documents.length > 0 && selected.size === documents.length;
  const someSelected = selected.size > 0 && !allSelected;

  // Hover preview (Extracted Fields) — anchored to the hovered Document ID.
  //
  // The trigger and the popup are two separate DOM elements with a gap
  // between them (the Popper's offset). Closing on the trigger's plain
  // onMouseLeave fired the instant the mouse left the trigger — before it had
  // crossed that gap to reach the popup — so the popup vanished out from
  // under the cursor and could never be reached. A short close delay, armed
  // on leaving EITHER the trigger or the popup and cancelled on entering
  // EITHER, treats the two as one hoverable region: moving through the gap
  // between them cancels the pending close, moving away from both lets it
  // fire.
  const [hover, setHover] = useState({ anchorEl: null, doc: null });
  const closeTimerRef = useRef(null);
  const cancelHoverClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openHover = (event, doc) => {
    cancelHoverClose();
    setHover({ anchorEl: event.currentTarget, doc });
  };
  const scheduleCloseHover = () => {
    cancelHoverClose();
    closeTimerRef.current = setTimeout(() => setHover({ anchorEl: null, doc: null }), 150);
  };

  // Per-row action menu. FSD ("Action Menu" -> Behavior): "Once an action has
  // been taken on a row, the row's action control displays that action until
  // a different one is chosen" — tracked here by doc.id so each row's button
  // keeps showing its last-taken action instead of reverting to the
  // "Select action" placeholder.
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, doc: null });
  const [rowActionLabel, setRowActionLabel] = useState({});
  // Per-row in-flight download. Previously handleDownload fired
  // onDownloadDocument and immediately closed the menu with no await and no
  // state tracking at all — a single-document download had ZERO feedback: the
  // button relabelled to "Download document" instantly regardless of whether
  // the file bytes had actually arrived, so on a large file or slow network
  // there was nothing on screen to say a request was even in progress (only
  // the bulk toolbar button has its own spinner, via bulkDownloading in
  // index.jsx). Keyed by doc.id so this is independent per row, same pattern
  // as rowActionLabel.
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const closeActionMenu = () => setActionMenu({ anchorEl: null, doc: null });
  const handleDownload = async () => {
    const doc = actionMenu.doc;
    closeActionMenu();
    if (!onDownloadDocument || !doc) return;
    setDownloadingIds((prev) => new Set(prev).add(doc.id));
    try {
      await onDownloadDocument(doc);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.id);
        return next;
      });
      setRowActionLabel((prev) => ({ ...prev, [doc.id]: 'Download document' }));
    }
  };

  return (
    <div className={classes.tableWrap}>
      <Table size="small" stickyHeader className={classes.table}>
        {/* Fixed column widths (sum = 100%) so the table always fits the dialog
            width (no horizontal scroll) and each column is wide enough for its
            full header label + its cell content. Rebalanced for the XL dialog:
            Document Name (the longest value) gains the width; Level only ever
            holds a one-word chip so it gives width to Version, whose header is
            longer than its values. */}
        <colgroup>
          <col style={{ width: '4%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" className={classes.th}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onToggleAll}
                disabled={documents.length === 0}
              />
            </TableCell>
            <TableCell className={classes.th}>Document Name</TableCell>
            <TableCell className={classes.th}>Document Type</TableCell>
            <TableCell className={classes.th}>Document ID</TableCell>
            <TableCell className={classes.th}>Mosymphony ID</TableCell>
            <TableCell className={classes.th}>Level</TableCell>
            <TableCell className={classes.th}>Level ID</TableCell>
            <TableCell className={classes.th}>Version</TableCell>
            <TableCell className={classes.th}>Last Updated</TableCell>
            <TableCell className={classes.th} align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!viewSaved ? (
            <TableRow>
              <TableCell colSpan={10} className={classes.emptyRow}>
                Select your filters, then click "Save view" to load documents.
              </TableCell>
            </TableRow>
          ) : loading ? (
            <TableRow>
              <TableCell colSpan={10} className={classes.emptyRow}>
                Loading documents…
              </TableCell>
            </TableRow>
          ) : documents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className={classes.emptyRow}>
                No documents match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            documents.map((doc) => (
              <TableRow key={doc.id} hover selected={selected.has(doc.id)}>
                <TableCell padding="checkbox" className={classes.td}>
                  <Checkbox
                    size="small"
                    checked={selected.has(doc.id)}
                    onChange={() => onToggle(doc.id)}
                  />
                </TableCell>
                <TableCell className={classes.td}>
                  <span className={classes.docCell}>
                    <span className={classes.fileBadge}>{doc.fileType || 'PDF'}</span>
                    <span className={classes.docName}>{doc.name}</span>
                  </span>
                </TableCell>
                <TableCell className={classes.td}>
                  <span className={classes.docTypeCell}>{doc.docType || DASH}</span>
                </TableCell>
                <TableCell className={classes.td}>
                  <span className={classes.docTypeCell}>{doc.docId || DASH}</span>
                </TableCell>
                <TableCell className={classes.td}>
                  {doc.mosymphonyId ? (
                    <span
                      className={classes.docIdBtn}
                      onMouseEnter={(e) => openHover(e, doc)}
                      onMouseLeave={scheduleCloseHover}
                    >
                      {doc.mosymphonyId}
                    </span>
                  ) : (
                    <span>{DASH}</span>
                  )}
                </TableCell>
                <TableCell className={classes.td}>
                  <span className={classes.levelChip}>{doc.level}</span>
                </TableCell>
                <TableCell className={classes.td}>
                  <span className={classes.levelIdCell}>{doc.levelId || DASH}</span>
                </TableCell>
                <TableCell className={classes.td}>{doc.version}</TableCell>
                <TableCell className={classes.td}>{doc.lastUpdated}</TableCell>
                <TableCell className={classes.td} align="right">
                  {downloadingIds.has(doc.id) ? (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled
                      className={classes.actionBtn}
                      startIcon={<CircularProgress size={14} />}
                    >
                      Downloading…
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.actionBtn}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, doc })}
                    >
                      {rowActionLabel[doc.id] || 'Select action'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* ---- Extracted Fields hover preview (read-only) ---- */}
      <Popper
        open={Boolean(hover.anchorEl)}
        anchorEl={hover.anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1600 }}
        modifiers={{
          offset: { enabled: true, offset: '0,6' },
          flip: { enabled: true },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' },
        }}
      >
        <Paper
          className={classes.extractedPanel}
          elevation={0}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={scheduleCloseHover}
        >
          <div className={classes.extractedHeading}>Extracted Fields</div>
          {/* Only this list scrolls (extractedPanel's maxHeight bounds it) — the
              heading stays visible above it the whole time, so a long field
              list never pushes the "Extracted Fields" label out of view. */}
          <div className={classes.extractedList}>
            {hover.doc?.mosymphonyId ? (
              <div className={classes.extractedRow}>
                <span className={classes.extractedLabel}>Mosymphony ID</span>
                <span className={classes.extractedValue}>{hover.doc.mosymphonyId}</span>
              </div>
            ) : null}
            {(hover.doc?.extractedFields || []).map((field, idx) => (
              <div key={`${field.label}-${idx}`} className={classes.extractedRow}>
                <span className={classes.extractedLabel}>{field.label}</span>
                <span className={classes.extractedValue}>
                  {field.value == null || field.value === '' ? DASH : field.value}
                </span>
              </div>
            ))}
          </div>
        </Paper>
      </Popper>

      {/* ---- row action menu ---- */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={closeActionMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Review workspace (fields / queries) is out of scope for this release. */}
        <MenuItem disabled className={classes.actionMenuItem}>
          Review fields
        </MenuItem>
        <MenuItem disabled className={classes.actionMenuItem}>
          Review queries
        </MenuItem>
        <MenuItem className={classes.actionMenuItem} onClick={handleDownload}>
          Download document
        </MenuItem>
      </Menu>
    </div>
  );
};

export default DocumentTable;
